import * as _ from 'underscore';
import { appStore } from '../../app/AppStore';
import { siteUrlsSelector } from '../../app/selectors/SiteUrlsSelectors';
import { watchWithUndo } from '../../common/utilities/WatchWithUndo';
import { ISiteAdminSettings, ISiteSettingsForSiteAdmin } from '../../settings/types/ISiteSettingsForSiteAdmin';
import { ActionContext } from '../helpers/ActionContext';
import { Module } from '../Module';
import { SiteUsers } from '../../userManagement/services/resources/SiteUsers';
import {
  IUpdateSiteSettingsForSiteAdminParams,
  UpdateSiteSettingsForSiteAdminRequest,
} from '../ServerApi/requests/UpdateSiteSettingsForSiteAdminRequest';
import { ISiteUser } from '../ServerApi/types/ISiteUser';
import { ConfirmActionDialog } from '../services/ConfirmActionDialog';
import { ServerService } from '../services/ServerService';
import {
  AuthenticationProviderType,
  IAuthenticationProvider,
  ISiteAuthenticationSettings,
  SiteAuthenticationSettingsService,
} from '../services/SiteAuthenticationSettingsService';
import { ToasterService, ToastType } from '../services/ToasterService';
import { HelpService } from '../../commonInfra/services/HelpService';
import { WindowLocationService } from '../../commonInfra/services/WindowLocationService';
import { ServerSettingsUnauthenticated } from '../../settings/services/ServerSettingsUnauthenticated';
import { AddUsersButtonProps } from '../../userManagement/components/AddUsersButton';
import { Messages } from '../../codegen/Localize';

interface IActionDialogSiteAuthenticationProviderEnableScope extends ng.IScope {
  aboutToBeDisabled: IAuthenticationProvider[];
  aboutToLockMyselfOut: boolean;
}

export interface ISiteAuthenticationSettingsCtrlScope extends ng.IScope {
  download: (evt: Event, url: string) => void;
  enableProvider: (id: string) => ng.IPromise<void>;
  getHelp: (category?: string) => void;
  getProviderDescription: (provider: IAuthenticationProvider) => string;
  getProviderDisplayName: (provider: IAuthenticationProvider) => string;
  isConnectedClientsVisible: boolean;
  isEmbeddedVizForSiteSamlSupported: boolean;
  isExternalProviderEnabled: boolean;
  isExternalProviderEnabledChanged: () => void;
  isTableauIdSupported: boolean;
  siteAuthenticationSettings: ISiteAuthenticationSettings;
  siteSettingsForSiteAdmin: ISiteSettingsForSiteAdmin;
  usersUrl: string;
  addUsersButtonProps: AddUsersButtonProps;
}

// controller for siteAuthenticationSettings.pug
export class SiteAuthenticationSettingsCtrl {
  public static $inject = [
    '$q',
    '$scope',
    '$translate',
    '$window',
    'ConfirmActionDialog',
    'ServerService',
    'SiteAuthenticationSettingsService',
  ];

  constructor(
    private $q: ng.IQService,
    private $scope: ISiteAuthenticationSettingsCtrlScope,
    private $translate: ng.translate.ITranslateService,
    private $window: ng.IWindowService,
    private confirmActionDialog: ConfirmActionDialog,
    private server: ServerService,
            siteAuthenticationSettingsService: SiteAuthenticationSettingsService,
  ) {
    $scope.download = (evt: Event, url: string) => {
      evt.preventDefault();
      WindowLocationService.download(url);
    };
    $scope.enableProvider = (id: string) => {
      return this.showEnableProviderWarning(id)
        .then(() => siteAuthenticationSettingsService.enableProvider(id))
        .then(() => {
          siteAuthenticationSettingsService.getSiteAuthenticationSettings().then((siteAuthenticationSettings: ISiteAuthenticationSettings) => {
            $scope.siteAuthenticationSettings = siteAuthenticationSettings;
            $scope.isExternalProviderEnabled = (id !== siteAuthenticationSettings.defaultProvider.id);
          });
        });
    };
    $scope.getHelp = (category?: string) => {
      const url = HelpService.getServerHelpUrlByCategory(category);
      this.$window.open(url, '_blank');
    };
    $scope.getProviderDescription = (provider: IAuthenticationProvider) => {
      switch(provider.type) {
        case AuthenticationProviderType.oidc:
          return $translate.instant('ssoAuthTypeOIDC_description');
        case AuthenticationProviderType.saml:
          return $translate.instant('ssoAuthTypeSAML_description');
        case AuthenticationProviderType.default:
          return $translate.instant('ssoAuthTypeDefault_description');
      }
    };
    $scope.getProviderDisplayName = (provider: IAuthenticationProvider) => {
      return provider
        ? siteAuthenticationSettingsService.getProviderDisplayName(provider)
        : $translate.instant('siteAuthenticationTypeSSO');
    };
    $scope.isConnectedClientsVisible = ServerSettingsUnauthenticated.isConnectedClientsVisible();
    $scope.isEmbeddedVizForSiteSamlSupported = ServerSettingsUnauthenticated.isEmbeddedVizForSiteSamlSupported();
    $scope.isExternalProviderEnabled = !!_.find($scope.siteAuthenticationSettings.externalProviders, (provider: IAuthenticationProvider) => provider.isEnabled);
    $scope.isExternalProviderEnabledChanged = () => {
      const newVal = !$scope.isExternalProviderEnabled;
      const provider = (newVal ?
        $scope.siteAuthenticationSettings.externalProviders[0] :
        $scope.siteAuthenticationSettings.defaultProvider);
      $scope.enableProvider(provider.id).then(() => {
        $scope.isExternalProviderEnabled = newVal;
      });
    };
    $scope.isTableauIdSupported = ServerSettingsUnauthenticated.isTableauIdSupported();
    $scope.addUsersButtonProps = {
      isServerUsersSpace: false,
      addButtonText: Messages.siteAuthenticationStep6(),
      showSelectExistingUsersOption: true
    };
    $scope.siteAuthenticationSettings = $scope.siteAuthenticationSettings;
    $scope.siteSettingsForSiteAdmin =  $scope.siteSettingsForSiteAdmin;

    const urls = siteUrlsSelector(appStore.getState());
    $scope.usersUrl = urls.usersUrl();

    watchWithUndo($scope, 'siteAuthenticationSettings.providerIdForEmbeddedViews', (val: string) => {
      return this.notifyOnFail(siteAuthenticationSettingsService.setProviderIdForEmbeddedViews(val));
    });

    watchWithUndo($scope, 'siteSettingsForSiteAdmin', (val: ISiteSettingsForSiteAdmin) => {
      const params: IUpdateSiteSettingsForSiteAdminParams = {
          id: (val as any).id, // remove cast with tfs 792889
          siteAdminSettings: val.siteAdminSettings as ISiteAdminSettings,
          subscriptionSettings: val.subscriptionSettings
      };

      return this.notifyOnFail(server.sendRequest(new UpdateSiteSettingsForSiteAdminRequest(params)));
    }, true);
  }

  private showEnableProviderWarning(id: string): ng.IPromise<any> {
    return SiteUsers.instance.getById(this.server.sessionUserInfo().id).then((me: ISiteUser) => {
      const dialogScope = <IActionDialogSiteAuthenticationProviderEnableScope>this.$scope.$new();
      dialogScope.aboutToBeDisabled = _.filter(
        this.$scope.siteAuthenticationSettings.externalProviders,
        (provider: IAuthenticationProvider) => provider.isEnabled && provider.id !== id);
      dialogScope.aboutToLockMyselfOut = !!_.find(
        dialogScope.aboutToBeDisabled,
        (provider: IAuthenticationProvider) => !!me && provider.id === me.authenticationProviderId);

      if (dialogScope.aboutToBeDisabled.length === 0) {
        return this.$q.when();
      }

      return this.confirmActionDialog.open({
        action: () => undefined,
        actionName: this.$translate.instant('continueMessage'),
        title: this.$translate.instant('ssoBeforeYouContinue_title'),
        bodyTemplateUrl: 'confirmActionDialogSiteAuthenticationProviderEnable.html',
        scope: dialogScope,
        confirmDisabled: () => dialogScope.aboutToLockMyselfOut
      }).result;
    });
  }

  private notifyOnFail<TResult>(result: ng.IPromise<TResult>, translateKey?: string): ng.IPromise<TResult> {
    result.catch(() => {
      ToasterService.instance.pop({ type: ToastType.Error, text: this.$translate.instant(translateKey || 'unexpectedError') });
    });
    return result;
  }
}

Module.instance().controller('SiteAuthenticationSettingsCtrl', SiteAuthenticationSettingsCtrl);
